import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, KeyValuePipe, NgClass } from '@angular/common';
import { BackofficeService } from '@services/backoffice/backoffice.service';
import { PatientAggregateListItem, PipelineStatsSummary } from '@interfaces/backoffice';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'app-aggregates',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    KeyValuePipe,
    NgClass,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    NzTagModule,
    NzIconModule,
    NzModalModule,
    NzCardModule,
    NzStatisticModule,
    NzSpinModule,
    NzDividerModule,
    NzProgressModule,
    NzToolTipModule,
  ],
  templateUrl: './aggregates.component.html',
  styleUrl: './aggregates.component.scss',
})
export class AggregatesComponent implements OnInit {
  private readonly svc = inject(BackofficeService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);

  loading = signal(false);
  aggregates = signal<PatientAggregateListItem[]>([]);
  total = signal(0);
  pageIndex = signal(1);
  pageSize = signal(50);

  stats = signal<PipelineStatsSummary | null>(null);
  statsLoading = signal(false);

  searchPatientId = '';

  ngOnInit(): void {
    this.loadStats();
    this.loadAggregates();
  }

  loadStats(): void {
    this.statsLoading.set(true);
    this.svc.statsSummary().subscribe({
      next: (res) => {
        this.stats.set(res);
        this.statsLoading.set(false);
      },
      error: () => {
        this.statsLoading.set(false);
        this.message.error('Failed to load pipeline stats');
      },
    });
  }

  loadAggregates(): void {
    this.loading.set(true);
    const limit = this.pageSize();
    const offset = (this.pageIndex() - 1) * limit;
    this.svc.aggregatesList({ limit, offset }).subscribe({
      next: (res) => {
        this.aggregates.set(res.aggregates);
        this.total.set(res.total);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.message.error('Failed to load aggregates');
      },
    });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.pageIndex.set(params.pageIndex ?? 1);
    this.pageSize.set(params.pageSize ?? 50);
    this.loadAggregates();
  }

  searchById(): void {
    const id = this.searchPatientId.trim();
    if (!id) return;
    this.svc.aggregateDetail(id).subscribe({
      next: (detail) => {
        this.modal.info({
          nzTitle: `Aggregate: ${detail.patient_name ?? detail.patient_id}`,
          nzContent: `<pre style="max-height:500px;overflow:auto;font-size:12px">${JSON.stringify(detail, null, 2)}</pre>`,
          nzWidth: 800,
        });
      },
      error: (err) => this.message.error(err.error?.message || 'Aggregate not found'),
    });
  }

  refreshAggregate(patientId: string): void {
    this.svc.aggregateRefresh(patientId).subscribe({
      next: (res) => {
        this.message.success(res.message);
        this.loadAggregates();
        this.loadStats();
      },
      error: (err) => this.message.error(err.error?.message || 'Refresh failed'),
    });
  }

  viewDetail(item: PatientAggregateListItem): void {
    this.svc.aggregateDetail(item.patient_id).subscribe({
      next: (detail) => {
        this.modal.info({
          nzTitle: `Aggregate: ${detail.patient_name ?? detail.patient_id}`,
          nzContent: `<pre style="max-height:500px;overflow:auto;font-size:12px">${JSON.stringify(detail, null, 2)}</pre>`,
          nzWidth: 800,
        });
      },
      error: () => this.message.error('Failed to load aggregate detail'),
    });
  }

  funnelPercent(part: number, total: number): number {
    return total > 0 ? Math.round((part / total) * 100) : 0;
  }

  statusColor(status: string): string {
    switch (status) {
      case 'completed': return 'green';
      case 'processing': return 'blue';
      case 'queued': return 'orange';
      case 'failed': return 'red';
      case 'not_started': return 'default';
      default: return 'default';
    }
  }
}
